import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { BellRing } from "lucide-react";
import { fetchHome, fetchNews, CATEGORIES } from "@/lib/api";
import { useNow } from "@/hooks/useNow";
import { Ticker } from "@/components/news/Ticker";
import { Slider } from "@/components/news/Slider";
import { LiveFeed } from "@/components/news/LiveFeed";
import { NewsCard, NewsRow } from "@/components/news/NewsCard";
import { CategorySection, SectionTitle } from "@/components/news/CategorySection";
import { AdSlot } from "@/components/news/Ads";
import { GridSkeleton, HeroSkeleton } from "@/components/news/Skeletons";

const SITE_LD = (origin) => JSON.stringify({
  "@context": "https://schema.org", "@type": "WebSite", name: "Notícia 365", url: origin,
  potentialAction: { "@type": "SearchAction", target: `${origin}/pesquisa?q={search_term_string}`, "query-input": "required name=search_term_string" },
});

function useLive(initial) {
  const [latest, setLatest] = useState([]);
  const [fresh, setFresh] = useState(new Set());
  const ids = useRef(new Set());
  useEffect(() => { setLatest(initial); ids.current = new Set(initial.map((a) => a.id)); }, [initial]);
  useEffect(() => {
    const t = setInterval(async () => {
      const { items } = await fetchNews({ limit: 20 }).catch(() => ({ items: [] }));
      const news = items.filter((a) => !ids.current.has(a.id));
      if (!news.length) return;
      news.forEach((a) => ids.current.add(a.id));
      setFresh((f) => new Set([...f, ...news.map((a) => a.id)]));
      setLatest((l) => [...news, ...l].sort((x, y) => y.date.localeCompare(x.date)).slice(0, 30));
    }, 30000);
    return () => clearInterval(t);
  }, []);
  return { latest, fresh };
}

export default function Home() {
  const [d, setD] = useState(null);
  useNow();
  const load = useCallback(() => fetchHome().then((r) => { setD(r); if (!r.latest.length) setTimeout(load, 4000); }), []);
  useEffect(() => { load(); }, [load]);
  const { latest, fresh } = useLive(d?.latest || []);

  return (
    <div>
      <Helmet>
        <title>Notícia 365 | Notícias de Moçambique ao minuto</title>
        <meta name="description" content="Últimas notícias de Moçambique ao minuto: política, economia, desporto, sociedade e mundo. Todos os jornais moçambicanos num só lugar." />
        <link rel="canonical" href={window.location.origin + "/"} />
        <script type="application/ld+json">{SITE_LD(window.location.origin)}</script>
      </Helmet>
      <Ticker items={latest.slice(0, 10)} />
      {fresh.size > 0 && (
        <div data-testid="new-articles-banner" className="rise sticky top-[118px] z-30 mx-auto mt-3 flex w-fit items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-bold text-white shadow-lg">
          <BellRing size={14} /> {fresh.size} {fresh.size === 1 ? "nova notícia" : "novas notícias"} acabaram de chegar
        </div>
      )}
      <div className="mx-auto max-w-7xl space-y-12 px-4 pt-6 sm:px-6 lg:px-8">
        <AdSlot slot="header_banner" />
        {!d ? <HeroSkeleton /> : (
          <div className="grid gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8"><Slider items={d.slider} /></div>
            <div className="lg:col-span-4"><LiveFeed items={latest} fresh={fresh} /></div>
          </div>
        )}
        <section>
          <SectionTitle>Em destaque</SectionTitle>
          {!d ? <GridSkeleton n={4} /> : (
            <div data-testid="highlights-grid" className="grid gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
              {d.highlights.map((a, i) => <NewsCard key={a.id} a={a} i={i} />)}
            </div>
          )}
        </section>
        <AdSlot slot="home_between" />
        {d && (
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="space-y-14 lg:col-span-8">
              {CATEGORIES.map((c, i) => (
                <Fragment key={c.slug}>
                  <CategorySection slug={c.slug} items={d.sections[c.slug] || []} />
                  {i === 3 && <AdSlot slot="home_between" />}
                </Fragment>
              ))}
            </div>
            <aside className="space-y-10 lg:col-span-4">
              <div className="lg:sticky lg:top-32 space-y-10">
                <div data-testid="most-read">
                  <SectionTitle>Mais lidas</SectionTitle>
                  <div className="divide-y">{d.popular.map((a, i) => <NewsRow key={a.id} a={a} n={i + 1} />)}</div>
                </div>
                <AdSlot slot="sidebar" />
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
